import React, { useState, useEffect, memo } from 'react';
import { Tooltip } from 'antd';

// Import assets
import flag from '../../assets/footer/flag.png';
import coa from '../../assets/footer/coa.png';
import phoneIcon from '../../assets/icons/phone-solid.svg';
import userIcon from '../../assets/icons/user-solid1.svg';
import util from '../../../utils/util';

// Constants for better maintainability
const FOOTER_CONFIG = {
  tollFreeNumber: '+2404 7712',
  images: {
    flag: {
      src: flag,
      alt: 'National Flag',
      className: 'rounded shadow-sm'
    },
    coa: {
      src: coa,
      alt: 'Coat of Arms',
      className: 'rounded shadow-sm'
    }
  },
  icons: {
    phone: {
      src: phoneIcon,
      alt: 'Phone',
      height: 12,
      width: 12
    },
    user: {
      src: userIcon,
      alt: 'User',
      height: 12,
      width: 12
    }
  }
};

// Reusable Icon component
const FooterIcon = memo(({ icon, className = '' }) => (
  <img
    src={icon.src}
    alt={icon.alt}
    height={icon.height}
    width={icon.width}
    className={`footer-icon ${className}`}
    style={{ 
      marginRight: '4px', 
      verticalAlign: 'middle',
      filter: 'opacity(0.8)'
    }}
  />
));

// Logo component for national symbols
const NationalSymbols = memo(() => (
  <div className="national-symbols d-flex align-items-center">
    <Tooltip title="National Flag" placement="top">
      <div className="symbol-container me-2">
        <img
          src={FOOTER_CONFIG.images.flag.src}
          alt={FOOTER_CONFIG.images.flag.alt}
          className={`national-symbol ${FOOTER_CONFIG.images.flag.className}`}
          height={24}
          width="auto"
          loading="lazy"
          onError={(e) => {
            console.warn('Flag image failed to load');
            e.target.style.display = 'none';
          }}
        />
      </div>
    </Tooltip>
    
    <Tooltip title="Coat of Arms" placement="top">
      <div className="symbol-container">
        <img
          src={FOOTER_CONFIG.images.coa.src}
          alt={FOOTER_CONFIG.images.coa.alt}
          className={`national-symbol ${FOOTER_CONFIG.images.coa.className}`}
          height={24}
          width="auto"
          loading="lazy"
          onError={(e) => {
            console.warn('COA image failed to load');
            e.target.style.display = 'none';
          }}
        />
      </div>
    </Tooltip>
  </div>
));

// Contact and user info component
const FooterInfo = memo(() => {
  const [userInfo, setUserInfo] = useState({
    name: '',
    isLoading: true,
    error: false
  });

  useEffect(() => {
    try {
      const userName = util.getLoggedName();
      setUserInfo({
        name: userName || 'Guest User',
        isLoading: false,
        error: false
      });
    } catch (error) {
      console.error('Error getting user info:', error);
      setUserInfo({
        name: 'Guest User',
        isLoading: false,
        error: true
      });
    }
  }, []);

  return (
    <div className="footer-info text-primary d-flex align-items-center gap-3">
      {/* Help Line */}
      <div className="contact-info d-flex align-items-center">
        <FooterIcon icon={FOOTER_CONFIG.icons.phone} />
        <span className="footer-text">
          <strong>Help Line:</strong>{' '}
          <a 
            href={`tel:${FOOTER_CONFIG.tollFreeNumber.replace(/\s+/g, '')}`}
            className="phone-link text-primary text-decoration-none"
            title="Call toll free help line"
          >
            {FOOTER_CONFIG.tollFreeNumber}
          </a>
        </span>
      </div>

      {/* User Info */}
      <div className="user-info d-flex align-items-center">
        <FooterIcon icon={FOOTER_CONFIG.icons.user} />
        <span className="footer-text">
          <strong>User:</strong>{' '}
          <Tooltip title={userInfo.name} placement="top">
            <span className="user-name">
              {userInfo.isLoading ? (
                <span className="loading-text">Loading...</span>
              ) : userInfo.error ? (
                <span className="error-text">Unable to load</span>
              ) : (
                userInfo.name
              )}
            </span>
          </Tooltip>
        </span>
      </div>
    </div>
  );
});

const Footer = memo(() => {
  return (
    <footer className="app-footer" role="contentinfo">
      <div className="footer-container shadow-sm bg-light border-top">
        <div className="container-fluid">
          <div className="footer-content d-flex justify-content-between align-items-center py-2 px-3">
            {/* National Symbols Section */}
            <div className="footer-left d-flex align-items-center">
              <NationalSymbols />
            </div>

            {/* Contact and User Info Section */}
            <div className="footer-right">
              <FooterInfo />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced CSS Styles */}
      <style jsx>{`
        .app-footer {
          margin-top: auto;
          position: relative;
          z-index: 0; /* Place footer behind navigation */
        }

        .footer-container {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-top: 1px solid #dee2e6;
          min-height: 45px;
          margin-left: 250px; /* Push footer to start after navigation panel */
          padding-left: 0;
        }

        .footer-content {
          min-height: 40px;
        }

        .national-symbols {
          flex-shrink: 0;
        }

        .symbol-container {
          transition: transform 0.2s ease, opacity 0.2s ease;
          cursor: pointer;
        }

        .symbol-container:hover {
          transform: translateY(-1px);
          opacity: 0.9;
        }

        .national-symbol {
          max-height: 24px;
          height: auto;
          object-fit: contain;
          transition: all 0.2s ease;
        }

        .footer-info {
          color: #2d5f8b !important;
          font-size: 12px;
          line-height: 1.2;
          flex-wrap: wrap;
        }

        .footer-text {
          display: inline-flex;
          align-items: center;
          font-weight: 500;
          white-space: nowrap;
        }

        .phone-link {
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .phone-link:hover {
          color: #1a4971 !important;
          text-decoration: underline !important;
        }

        .user-name {
          font-weight: 600;
          max-width: 120px;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: middle;
        }

        .loading-text {
          color: #6c757d;
          font-style: italic;
        }

        .error-text {
          color: #dc3545;
          font-style: italic;
        }

        .footer-icon {
          transition: all 0.2s ease;
        }

        .footer-icon:hover {
          filter: opacity(1) !important;
          transform: scale(1.05);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .footer-container {
            margin-left: 0 !important; /* Remove left margin on mobile when sidebar collapses */
          }
          
          .footer-content {
            flex-direction: column;
            text-align: center !important;
            gap: 8px;
            padding: 10px 15px !important;
          }

          .footer-info {
            flex-direction: column;
            gap: 5px !important;
            font-size: 11px;
          }

          .national-symbols {
            justify-content: center;
          }

          .user-name {
            max-width: 100px;
          }
        }

        @media (max-width: 576px) {
          .footer-content {
            padding: 8px 10px !important;
          }

          .national-symbol {
            max-height: 20px;
          }

          .footer-info {
            font-size: 10px;
          }

          .symbol-container.me-2 {
            margin-right: 8px !important;
          }
        }

        /* Print styles */
        @media print {
          .app-footer {
            position: fixed;
            bottom: 0;
            width: 100%;
            background: white !important;
            border-top: 1px solid #000;
          }

          .footer-container {
            background: white !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;